/**
 * ACModalClassic — Monolithic AC device modal template.
 *
 * Complete AC control panel in a single file:
 * - Power toggle (OnOff)
 * - Temperature dial (+/- with hold-to-repeat)
 * - Mode selector (Cool/Heat/Dry/Fan/Auto)
 * - Fan speed (Low/Med/High/Auto)
 * - Sensor readout (temperature, humidity)
 *
 * This is the first example of the new "monolithic modal" architecture.
 * Developers can import trait components from traitStyles/ for reuse,
 * or write everything from scratch like this file does.
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { TemperatureChartStyle01 } from '../components/datacharts/TemperatureChartStyle01'
import { executeCommandWithAuth } from '../services/turingMcpApi'
import './ACModalClassic.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const AC_CLASSIC_SECTIONS: ModalSectionMeta[] = [
  // ── Modal Background (header + overall) ──
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [
    { id: 'header-back-icon', label: 'Back Arrow Icon', editableProps: ['color', 'fontSize'] },
    { id: 'header-title', label: 'Device Name Text', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'header-edit-icon', label: 'Edit Icon', editableProps: ['color', 'fontSize'] },
  ]},
  // ── Power Section ──
  { id: 'power', label: 'Power', icon: 'power_settings_new', slots: [
    { id: 'power-label', label: 'Title "POWER STATE"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing', 'textTransform'] },
    { id: 'power-value', label: 'Status Text "ON/OFF"', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
    { id: 'power-btn', label: 'Power Button Circle', editableProps: ['background', 'color', 'borderRadius', 'width', 'height', 'boxShadow', 'glow'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
    { id: 'power-btn-icon', label: 'Power Button Icon', editableProps: ['fontSize', 'color'],
      states: [{ id: 'on', label: 'ON' }, { id: 'off', label: 'OFF' }] },
  ]},
  // ── Temperature Section ──
  { id: 'temperature', label: 'Temperature', icon: 'thermostat', slots: [
    { id: 'temp-label', label: 'Title "TEMPERATURE"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'sensor-row', label: 'Sensor Row Container', editableProps: ['background', 'borderRadius', 'padding', 'border'] },
    { id: 'sensor-row-icon', label: 'Sensor Row Icon', editableProps: ['fontSize', 'color'] },
    { id: 'sensor-row-label', label: 'Sensor Row "SENSOR"', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'sensor-row-value', label: 'Sensor Row Value "25.0°C"', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'temp-value', label: 'Target Temp Number', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'temp-unit', label: 'Unit "°C"', editableProps: ['fontSize', 'color'] },
    { id: 'temp-btn', label: '+/- Button Circle', editableProps: ['background', 'color', 'borderRadius', 'width', 'height', 'border'] },
    { id: 'temp-btn-icon', label: '+/- Button Icon', editableProps: ['fontSize', 'color'] },
  ]},
  // ── Mode Section ──
  { id: 'mode', label: 'Mode', icon: 'ac_unit', slots: [
    { id: 'mode-label', label: 'Title "Mode"', editableProps: ['fontSize', 'color', 'fontWeight'] },
    { id: 'mode-btn', label: 'Mode Button', editableProps: ['background', 'color', 'borderRadius', 'padding', 'fontSize', 'border', 'glow'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
    { id: 'mode-btn-icon', label: 'Mode Button Icon', editableProps: ['fontSize', 'color'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
    { id: 'mode-btn-text', label: 'Mode Button Text', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  // ── Fan Speed Section ──
  { id: 'fan', label: 'Fan Speed', icon: 'mode_fan', slots: [
    { id: 'fan-label-text', label: 'Title "FAN SPEED"', editableProps: ['fontSize', 'color', 'fontWeight', 'letterSpacing'] },
    { id: 'fan-label-icon', label: 'Fan Title Icon', editableProps: ['fontSize', 'color'] },
    { id: 'fan-btn', label: 'Speed Button', editableProps: ['background', 'color', 'borderRadius', 'padding', 'fontSize', 'border', 'glow'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
    { id: 'fan-btn-text', label: 'Speed Button Text', editableProps: ['fontSize', 'color', 'fontWeight'],
      states: [{ id: 'default', label: 'Default' }, { id: 'active', label: 'Active' }] },
  ]},
  // ── Sensors Section ──
  { id: 'sensors', label: 'Sensors', icon: 'sensors', slots: [
    { id: 'sensor-card', label: 'Sensor Card Container', editableProps: ['background', 'borderRadius', 'padding', 'border', 'gap'] },
    { id: 'sensor-card-icon', label: 'Sensor Card Icon', editableProps: ['fontSize', 'color'] },
    { id: 'sensor-card-value', label: 'Sensor Card Value', editableProps: ['fontSize', 'color', 'fontWeight'] },
  ]},
  // ── Temperature History Chart ──
  { id: 'temp-chart', label: 'Temp History', icon: 'show_chart', slots: [] },
]

const DEFAULT_SECTION_ORDER = AC_CLASSIC_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODES = [
  { id: 'cool', label: 'Cool', icon: 'ac_unit' },
  { id: 'heat', label: 'Heat', icon: 'local_fire_department' },
  { id: 'dry', label: 'Dry', icon: 'water_drop' },
  { id: 'fan-only', label: 'Fan', icon: 'air' },
  { id: 'auto', label: 'Auto', icon: 'hdr_auto' },
] as const

const FAN_SPEEDS = [
  { id: 'low', label: 'Low' },
  { id: 'medium', label: 'Med' },
  { id: 'high', label: 'High' },
  { id: 'auto', label: 'Auto' },
] as const

const TEMP_MIN = 16
const TEMP_MAX = 32

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAttr(state: Record<string, unknown> | null, ...keys: string[]): unknown {
  if (!state) return undefined
  for (const key of keys) {
    if (key in state && state[key] !== undefined && state[key] !== null) return state[key]
  }
  return undefined
}

function numAttr(state: Record<string, unknown> | null, ...keys: string[]): number | null {
  let v = getAttr(state, ...keys)
  if (v === undefined || v === null) return null
  // Handle array values like temperature: [26] — take first element
  if (Array.isArray(v)) v = v[0]
  if (v === undefined || v === null) return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

function strAttr(state: Record<string, unknown> | null, ...keys: string[]): string {
  const v = getAttr(state, ...keys)
  return v != null ? String(v) : ''
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ACModalClassic({
  device,
  deviceState,
  onCommand,
  onClose,
  isEditorMode,
  cssOverrides,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
  credentials,
}: DeviceModalTemplateProps) {
  // ---- Context menu for editor mode ----
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  // ---- Section ordering ----
  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    // Include any sections from default that aren't in the custom order
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- State from device ----
  const isOn = getAttr(deviceState, 'on', 'isOn', 'powerState') === true
    || strAttr(deviceState, 'on', 'isOn', 'powerState') === 'true'
  const targetTemp = numAttr(deviceState, 'thermostatTemperatureSetpoint', 'targetTemperature', 'temperature') ?? 24
  const currentTemp = numAttr(deviceState, 'temperatureAmbientCelsius', 'currentTemperature', 'ambientTemperature', 'temperature')
  const currentHumidity = numAttr(deviceState, 'humidityAmbientPercent', 'humidity')
  const currentMode = strAttr(deviceState, 'thermostatMode', 'mode') || 'cool'
  const currentFanSpeed = strAttr(deviceState, 'currentFanSpeedSetting', 'fanSpeed') || 'auto'

  // ---- Device Info panel ----
  const [showDeviceInfo, setShowDeviceInfo] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: String(device.type || 'Unknown') })
    if (device.ip) items.push({ label: 'IP ADDRESS', value: String(device.ip) })
    if (device.fwVersion || device.firmwareVersion) items.push({ label: 'FW VERSION', value: String(device.fwVersion || device.firmwareVersion) })
    if (device.rssi != null) items.push({ label: 'RSSI', value: String(device.rssi) })
    if (device.uplink_ssid) items.push({ label: 'UPLINK SSID', value: String(device.uplink_ssid) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
  }, [])

  // ---- Local UI state (optimistic updates) ----
  const [pendingTemp, setPendingTemp] = useState<number | null>(null)
  const [pendingMode, setPendingMode] = useState<string | null>(null)
  const [pendingFan, setPendingFan] = useState<string | null>(null)
  const [pendingPower, setPendingPower] = useState<boolean | null>(null)
  const displayTemp = pendingTemp ?? targetTemp
  const displayMode = pendingMode ?? currentMode
  const displayFan = pendingFan ?? currentFanSpeed
  const displayPower = pendingPower ?? isOn
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const holdIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Sync pending states when device responds
  useEffect(() => {
    if (pendingTemp !== null && pendingTemp === targetTemp) setPendingTemp(null)
  }, [targetTemp, pendingTemp])
  useEffect(() => {
    if (pendingMode !== null && pendingMode === currentMode) setPendingMode(null)
  }, [currentMode, pendingMode])
  useEffect(() => {
    if (pendingFan !== null && pendingFan === currentFanSpeed) setPendingFan(null)
  }, [currentFanSpeed, pendingFan])
  useEffect(() => {
    if (pendingPower !== null && pendingPower === isOn) setPendingPower(null)
  }, [isOn, pendingPower])

  // ---- Internal API caller (self-contained, no onCommand needed from parent) ----
  const execCmd = useCallback((cmd: string, params?: Record<string, unknown>) => {
    if (!credentials || !device.sn) return
    executeCommandWithAuth(credentials, [device.sn], cmd, params).catch(() => {})
    // Also call onCommand if parent provided it (backward compat)
    if (onCommand) onCommand(cmd, params)
  }, [credentials, device.sn, onCommand])

  // ---- Commands ----
  const togglePower = useCallback(() => {
    if (isEditorMode) return
    const next = !displayPower
    setPendingPower(next)
    execCmd(next ? 'on' : 'off')
  }, [isEditorMode, displayPower, execCmd])

  const setTemp = useCallback((temp: number) => {
    if (isEditorMode) return
    const clamped = Math.max(TEMP_MIN, Math.min(TEMP_MAX, temp))
    setPendingTemp(clamped)
    execCmd('setTemperature', { temperature: clamped })
  }, [isEditorMode, execCmd])

  const setMode = useCallback((mode: string) => {
    if (isEditorMode) return
    setPendingMode(mode)
    execCmd('setMode', { mode })
  }, [isEditorMode, execCmd])

  const setFanSpeed = useCallback((speed: string) => {
    if (isEditorMode) return
    setPendingFan(speed)
    execCmd('setFanSpeed', { fanSpeed: speed })
  }, [isEditorMode, execCmd])

  // Hold-to-repeat for +/- buttons
  const startHold = useCallback((delta: number) => {
    holdTimerRef.current = setTimeout(() => {
      holdIntervalRef.current = setInterval(() => {
        setPendingTemp((prev) => {
          const next = Math.max(TEMP_MIN, Math.min(TEMP_MAX, (prev ?? targetTemp) + delta))
          execCmd('setTemperature', { temperature: next })
          return next
        })
      }, 200)
    }, 400)
  }, [onCommand, targetTemp])

  const stopHold = useCallback(() => {
    if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null }
    if (holdIntervalRef.current) { clearInterval(holdIntervalRef.current); holdIntervalRef.current = null }
  }, [])

  return (
    <div
      className={`ac-modal-classic${displayPower ? '' : ' is-off'}${isEditorMode ? ' editor-mode' : ''}`}
      data-modal-template-id="ac-classic"
      onContextMenuCapture={handleContextMenu}
    >
      {cssOverrides && (
        <style dangerouslySetInnerHTML={{ __html: cssOverrides }} />
      )}

      {/* Header (always first, not reorderable) */}
      <div className="ac-modal-classic__header">
        <button className="ac-modal-classic__back" onClick={onClose} aria-label="Back">
          <span className="material-symbols-rounded" data-modal-slot="header-back-icon">arrow_back</span>
        </button>
        <h2 className="ac-modal-classic__title" data-modal-slot="header-title">{device.name || 'Air Conditioner'}</h2>
        <button
          className="ac-modal-classic__edit"
          onClick={() => {/* future: edit nickname */}}
          aria-label="Edit"
        >
          <span className="material-symbols-rounded" data-modal-slot="header-edit-icon">edit</span>
        </button>
        <button
          className="ac-modal-classic__info-btn"
          onClick={() => setShowDeviceInfo(true)}
          aria-label="Device Info"
        >
          <span className="material-symbols-rounded">info</span>
        </button>
      </div>

      {/* Device Info overlay */}
      {showDeviceInfo && (
        <div className="ac-modal-classic__device-info-overlay">
          <div className="ac-modal-classic__device-info-card">
            <div className="ac-modal-classic__device-info-header">
              <span>Device Info</span>
              <button onClick={() => setShowDeviceInfo(false)} aria-label="Close">
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            {deviceInfoItems.map((item) => (
              <div key={item.label} className="ac-modal-classic__device-info-row">
                <span className="ac-modal-classic__device-info-label">{item.label}</span>
                <span className="ac-modal-classic__device-info-value">
                  {item.value}
                  {item.action === 'copy' && (
                    <button
                      className="ac-modal-classic__device-info-action"
                      onClick={() => copyToClipboard(item.value)}
                      aria-label="Copy"
                    >
                      <span className="material-symbols-rounded">content_copy</span>
                    </button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sections rendered in order */}
      {orderedSections.map((sectionId) => {
        if (!isSectionVisible(sectionId)) return null
        const hl = highlightedSection === sectionId ? ' section-highlighted' : ''
        switch (sectionId) {
          case 'power':
            return (
              <div key="power" className={`ac-modal-classic__power-section${hl}`} data-modal-section="power">
                <div className="ac-modal-classic__power-label" data-modal-slot="power-label">POWER STATE</div>
                <div className="ac-modal-classic__power-row">
                  <span className={`ac-modal-classic__power-status${displayPower ? ' on' : ' off'}`} data-modal-slot="power-value" data-modal-slot-state={displayPower ? 'on' : 'off'}>
                    {displayPower ? 'ON' : 'OFF'}
                  </span>
                  <button
                    className={`ac-modal-classic__power-btn${displayPower ? ' on' : ''}`}
                    data-modal-slot="power-btn"
                    data-modal-slot-state={displayPower ? 'on' : 'off'}
                    onClick={togglePower}
                    aria-label={displayPower ? 'Turn off' : 'Turn on'}
                  >
                    <span className="material-symbols-rounded" data-modal-slot="power-btn-icon" data-modal-slot-state={displayPower ? 'on' : 'off'}>power_settings_new</span>
                  </button>
                </div>
              </div>
            )
          case 'temperature':
            return (
              <div key="temperature" className={`ac-modal-classic__temp-section${hl}`} data-modal-section="temperature">
                <div className="ac-modal-classic__temp-label" data-modal-slot="temp-label">TEMPERATURE</div>
                {currentTemp !== null && (
                  <div className="ac-modal-classic__sensor-row" data-modal-slot="sensor-row">
                    <span className="material-symbols-rounded" data-modal-slot="sensor-row-icon">thermostat</span>
                    <span data-modal-slot="sensor-row-label">SENSOR</span>
                    <span className="ac-modal-classic__sensor-value" data-modal-slot="sensor-row-value">{currentTemp.toFixed(1)}°C</span>
                  </div>
                )}
                <div className="ac-modal-classic__temp-dial">
                  <button
                    className="ac-modal-classic__temp-btn"
                    data-modal-slot="temp-btn"
                    onClick={() => setTemp(displayTemp - 1)}
                    onPointerDown={() => startHold(-1)}
                    onPointerUp={stopHold}
                    onPointerLeave={stopHold}
                    disabled={displayTemp <= TEMP_MIN}
                    aria-label="Decrease temperature"
                  >
                    <span className="material-symbols-rounded" data-modal-slot="temp-btn-icon">remove</span>
                  </button>
                  <div className="ac-modal-classic__temp-display">
                    <span className="ac-modal-classic__temp-value" data-modal-slot="temp-value">{displayTemp}</span>
                    <span className="ac-modal-classic__temp-unit" data-modal-slot="temp-unit">°C</span>
                  </div>
                  <button
                    className="ac-modal-classic__temp-btn"
                    data-modal-slot="temp-btn"
                    onClick={() => setTemp(displayTemp + 1)}
                    onPointerDown={() => startHold(1)}
                    onPointerUp={stopHold}
                    onPointerLeave={stopHold}
                    disabled={displayTemp >= TEMP_MAX}
                    aria-label="Increase temperature"
                  >
                    <span className="material-symbols-rounded" data-modal-slot="temp-btn-icon">add</span>
                  </button>
                </div>
              </div>
            )
          case 'mode':
            return (
              <div key="mode" className={`ac-modal-classic__mode-section${hl}`} data-modal-section="mode">
                <div className="ac-modal-classic__section-label" data-modal-slot="mode-label">Mode</div>
                <div className="ac-modal-classic__mode-grid">
                  {MODES.map((mode) => (
                    <button
                      key={mode.id}
                      className={`ac-modal-classic__mode-btn${displayMode === mode.id ? ' active' : ''}`}
                      data-modal-slot="mode-btn"
                      data-modal-slot-state={displayMode === mode.id ? 'active' : 'default'}
                      onClick={() => setMode(mode.id)}
                    >
                      <span className="material-symbols-rounded" data-modal-slot="mode-btn-icon" data-modal-slot-state={displayMode === mode.id ? 'active' : 'default'}>{mode.icon}</span>
                      <span data-modal-slot="mode-btn-text" data-modal-slot-state={displayMode === mode.id ? 'active' : 'default'}>{mode.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )
          case 'fan':
            return (
              <div key="fan" className={`ac-modal-classic__fan-section${hl}`} data-modal-section="fan">
                <div className="ac-modal-classic__section-label">
                  <span data-modal-slot="fan-label-text">FAN SPEED</span>
                  <span className="material-symbols-rounded ac-modal-classic__fan-icon" data-modal-slot="fan-label-icon">mode_fan</span>
                </div>
                <div className="ac-modal-classic__fan-grid">
                  {FAN_SPEEDS.map((speed) => (
                    <button
                      key={speed.id}
                      className={`ac-modal-classic__fan-btn${displayFan === speed.id ? ' active' : ''}`}
                      data-modal-slot="fan-btn"
                      data-modal-slot-state={displayFan === speed.id ? 'active' : 'default'}
                      onClick={() => setFanSpeed(speed.id)}
                    >
                      <span data-modal-slot="fan-btn-text" data-modal-slot-state={displayFan === speed.id ? 'active' : 'default'}>{speed.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )
          case 'sensors':
            if (currentTemp === null && currentHumidity === null) return null
            return (
              <div key="sensors" className={`ac-modal-classic__sensors${hl}`} data-modal-section="sensors">
                {currentTemp !== null && (
                  <div className="ac-modal-classic__sensor-card" data-modal-slot="sensor-card">
                    <span className="material-symbols-rounded" data-modal-slot="sensor-card-icon">thermostat</span>
                    <span data-modal-slot="sensor-card-value">{currentTemp.toFixed(1)}°C</span>
                  </div>
                )}
                {currentHumidity !== null && (
                  <div className="ac-modal-classic__sensor-card" data-modal-slot="sensor-card">
                    <span className="material-symbols-rounded" data-modal-slot="sensor-card-icon">humidity_percentage</span>
                    <span data-modal-slot="sensor-card-value">{currentHumidity.toFixed(0)}%</span>
                  </div>
                )}
              </div>
            )
          case 'temp-chart': {
            const chartSN = device.sn && !device.sn.startsWith('MOCK') ? device.sn : null
            return (
              <div key="temp-chart" className={`ac-modal-classic__temp-chart${hl}`} data-modal-section="temp-chart">
                <TemperatureChartStyle01
                  deviceSN={chartSN}
                  credentials={credentials}
                />
              </div>
            )
          }
          default:
            return null
        }
      })}
    </div>
  )
}
